import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

const FEATURES = [
  {
    id: 'profiles',
    title: 'Profiles & records',
    description: 'One profile per person — enrollment, family links, student information, and credentials.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    )
  },
  {
    id: 'courses',
    title: 'Courses & curriculum',
    description: 'Standards-aligned coursework, published once and adapted to each classroom.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    )
  },
  {
    id: 'ai',
    title: 'AI assistance, real educators',
    description: 'AI speeds up course building and feedback. Every course and grade is still administered by professionals.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 3l1.9 4.1L18 9l-4.1 1.9L12 15l-1.9-4.1L6 9l4.1-1.9L12 3z" />
        <path d="M18 15l.9 2.1L21 18l-2.1.9L18 21l-.9-2.1L15 18l2.1-.9L18 15z" />
      </svg>
    )
  },
  {
    id: 'cte',
    title: 'CTE pathways',
    description: 'Career and technical tracks run alongside core academics, on the same record.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20 7H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1z" />
        <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        <path d="M3 12h18" />
      </svg>
    )
  },
  {
    id: 'forms',
    title: 'Forms & attendance',
    description: 'Custom forms, attendance, and attachments attached to the right group and term.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    )
  },
  {
    id: 'access',
    title: 'Roles & access',
    description: 'Districts, schools, teachers, and families each see only what belongs to them.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    )
  }
];

const Home = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="home-page">
      <div className="home-inner">
        <header className="home-hero">
          <span className="home-badge">Districts · Schools · Families</span>
          <h1 className="home-title">
            Everything your organization runs on,{' '}
            <span className="home-title-accent">shaped to fit.</span>
          </h1>
          <p className="home-lead">
            Student records, courses, CTE pathways, and AI-assisted tools — configured around the way
            you&apos;re organized, whether that&apos;s a multi-school district, a single school, or one family.
          </p>
          <div className="home-actions">
            <Link to="/onboarding" className="btn-brand">
              Request access
              <span aria-hidden>→</span>
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className="btn-home-secondary">Dashboard</Link>
            ) : (
              <Link to="/auth" className="btn-home-secondary">Sign in</Link>
            )}
          </div>
          <p className="home-note">
            Access starts with a short form — we follow up with next steps.
            <Link to="/families">Enrolling your own child?</Link>
          </p>
        </header>

        <section className="home-features" aria-label="What you get">
          <div className="home-grid">
            {FEATURES.map((f) => (
              <article key={f.id} className="home-card">
                <span className="home-card-icon">{f.icon}</span>
                <h2 className="home-card-title">{f.title}</h2>
                <p className="home-card-text">{f.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="home-cta">
          <div className="home-cta-text">
            <h2 className="home-cta-title">Ready to get set up?</h2>
            <p className="home-cta-sub">Tell us about your district, school, or family and we&apos;ll shape it around you.</p>
          </div>
          <div className="home-actions">
            <Link to="/onboarding" className="btn-brand">
              Schools &amp; districts
              <span aria-hidden>→</span>
            </Link>
            <Link to="/families" className="btn-home-secondary">Families</Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
